// TMS Design System Colors
// Based on TMS-Design-System.md specifications

export const colors = {
  // Brand Colors
  brand: {
    primary: '#0066CC', // TMS Blue
    secondary: '#4285F4', // Action Blue
    dark: '#002147', // Navy Blue
  },

  // Semantic Colors
  semantic: {
    success: '#28A745', // Green
    warning: '#FFC107', // Amber
    danger: '#DC3545', // Red
    info: '#17A2B8', // Cyan
  },

  // Neutral Gray Scale
  gray: {
    900: '#212529', // Primary Text
    800: '#343A40', // Headers
    700: '#495057', // Secondary Headers
    600: '#6C757D', // Secondary Text
    500: '#ADB5BD', // Disabled Text
    400: '#CED4DA', // Borders
    300: '#DEE2E6', // Dividers
    200: '#E9ECEF', // Backgrounds
    100: '#F8F9FA', // Light Backgrounds
    50: '#FFFFFF', // White
  },

  // Accent Colors
  accent: {
    orange: '#FF6B35', // CTAs
    purple: '#6F42C1', // Premium Features
    teal: '#20C997', // Highlights
  },

  // Status Colors (for logistics operations)
  status: {
    pending: '#FFC107', // Pending orders/shipments
    inProgress: '#17A2B8', // In transit
    completed: '#28A745', // Delivered
    cancelled: '#DC3545', // Cancelled
    delayed: '#FF6B35', // Delayed deliveries
  },

  // Background Variants
  background: {
    primary: '#FFFFFF',
    secondary: '#F8F9FA',
    tertiary: '#E9ECEF',
    dark: '#212529',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  // Text Variants
  text: {
    primary: '#212529',
    secondary: '#6C757D',
    tertiary: '#ADB5BD',
    inverse: '#FFFFFF',
    link: '#0066CC',
    linkHover: '#004499',
  },

  // Border Variants
  border: {
    light: '#DEE2E6',
    default: '#CED4DA',
    strong: '#6C757D',
    focus: '#0066CC',
    error: '#DC3545',
    success: '#28A745',
  },
} as const;

// CSS Variables for dynamic theming
export const cssVariables = {
  '--color-brand-primary': colors.brand.primary,
  '--color-brand-secondary': colors.brand.secondary,
  '--color-brand-dark': colors.brand.dark,

  '--color-success': colors.semantic.success,
  '--color-warning': colors.semantic.warning,
  '--color-danger': colors.semantic.danger,
  '--color-info': colors.semantic.info,

  '--color-text-primary': colors.text.primary,
  '--color-text-secondary': colors.text.secondary,
  '--color-text-tertiary': colors.text.tertiary,
  '--color-text-inverse': colors.text.inverse,

  '--color-background-primary': colors.background.primary,
  '--color-background-secondary': colors.background.secondary,
  '--color-background-tertiary': colors.background.tertiary,

  '--color-border-light': colors.border.light,
  '--color-border-default': colors.border.default,
  '--color-border-strong': colors.border.strong,
} as const;

// Type definitions for better TypeScript support
export type Colors = typeof colors;
export type ColorKeys = keyof Colors;
export type BrandColors = keyof Colors['brand'];
export type SemanticColors = keyof Colors['semantic'];
export type GrayColors = keyof Colors['gray'];
export type AccentColors = keyof Colors['accent'];
export type StatusColors = keyof Colors['status'];
